import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { useSelector } from 'react-redux';

const baseURL = 'https://vjoneparivaar.com/api';

const formatPoints = (value) =>
  new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);

const PointsSummaryBanner = ({ balancePoints, onRedeem }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [earnedPoints, setEarnedPoints] = useState(0);
  const [loadingEarned, setLoadingEarned] = useState(true);
  const [loadingBalance, setLoadingBalance] = useState(true);

  const { fosId } = useSelector((state) => state.Auth.user);

  const axiosInstance = axios.create({
    baseURL,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true
  });

  useEffect(() => {
    const fetchTotalPoints = async () => {
      setLoadingEarned(true);
      try {
        const response = await axiosInstance.get(`/fosWallet/fosTotalPoints?fos_id=${fosId}`);
        setEarnedPoints(response.data.data.totalPoints);
      } catch (error) {
        console.error('Error fetching total points:', error);
        setEarnedPoints(0);
      } finally {
        setLoadingEarned(false);
      }
    };

    // Artificial delay for simulating balance loader
    const simulateBalanceLoading = () => {
      setLoadingBalance(true);
      setTimeout(() => {
        setLoadingBalance(false);
      }, 1000); // adjust as per real use-case
    };

    fetchTotalPoints();
    simulateBalanceLoading();
  }, [fosId]);

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        width: '100%',
        boxShadow: 2
      }}
    >
      {/* Earned Points */}
      <Box
        sx={{
          flex: 1,
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2.5 },
          background: 'linear-gradient(to bottom right, #dceeff, #a5d4ff)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <Box display="flex" alignItems="center" mb={0.5}>
          <EmojiEventsOutlinedIcon sx={{ color: '#0056d2', mr: 1, fontSize: { xs: 18, sm: 20 } }} />
          <Typography fontWeight="bold" sx={{ fontSize: { xs: '12px', sm: '14px' }, color: '#0056d2' }}>
            Earned Points:
          </Typography>
        </Box>
        {loadingEarned ? (
          <Box display="flex" alignItems="center">
            <CircularProgress size={20} sx={{ mr: 1, color: '#0045b5' }} />
            <Typography sx={{ fontSize: { xs: '14px', sm: '16px' }, color: '#0045b5' }}>
              Loading...
            </Typography>
          </Box>
        ) : (
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '18px', sm: '26px' },
              color: '#0045b5'
            }}
          >
            {formatPoints(earnedPoints)}
          </Typography>
        )}
      </Box>

      {/* Balance Points */}
      <Box
        sx={{
          flex: 1,
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2.5 },
          backgroundColor: '#f9fbff',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}
      >
        <Box>
          <Box display="flex" alignItems="center" mb={0.5}>
            <AccountBalanceWalletOutlinedIcon sx={{ color: '#0056d2', mr: 1, fontSize: { xs: 18, sm: 20 } }} />
            <Typography fontWeight="bold" sx={{ fontSize: { xs: '12px', sm: '14px' }, color: '#0056d2' }}>
              Balance Points:
            </Typography>
          </Box>
          {loadingBalance || balancePoints === undefined || balancePoints === null ? (
            <Box display="flex" alignItems="center">
              <CircularProgress size={20} sx={{ mr: 1, color: '#0045b5' }} />
              <Typography sx={{ fontSize: { xs: '14px', sm: '16px' }, color: '#0045b5' }}>
                Loading...
              </Typography>
            </Box>
          ) : (
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '18px', sm: '26px' },
                color: '#0045b5'
              }}
            >
              {formatPoints(balancePoints)}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            mt: { xs: 1, sm: 0 },
            display: 'flex',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            ml: { xs: 23, sm: 0 }
          }}
        >
          <Button
            variant="contained"
            onClick={onRedeem}
            disabled={loadingEarned || loadingBalance}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              background: '#1669f2',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontSize: {xs: "1.6rem", sm: "1.8rem"},
              '&:hover': {
                backgroundColor: '#1257cc'
              }
            }}
          >
            Redeem Now
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PointsSummaryBanner;
